import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";

import DashboardLayout from "../../Layout/DashboardLayout";
import { companyID, employeeID } from "../../Common/UIComponents";
import { URL } from "../../Common/APIURL";
import {
  fetchSetupUtilCreateData,
  fetchSetupUtilLabList,
  fetchSetupUtilTimesheetList,
} from "../../API_Service/LabBooking_Slice";

// ─────────────────────────────────────────────
// LAB CARD — click whole card to open
// ─────────────────────────────────────────────
function LabCard({ lab, isMapped, onOpen, opening }) {
  const [imgError, setImgError] = useState(false);
  const isOpening = opening === lab.facilityId;
  const isDisabled = !!opening && !isOpening;

  return (
    <button
      type="button"
      onClick={() => onOpen(lab)}
      disabled={!!opening}
      className="group relative w-full h-full text-left rounded-3xl overflow-hidden transition-all duration-300 focus:outline-none flex flex-col"
      style={{
        boxShadow: isOpening
          ? "0 0 0 3px #08757C, 0 20px 40px rgba(8,117,124,0.25)"
          : "0 4px 24px rgba(24,28,29,0.07)",
        transform: isDisabled ? "scale(0.98)" : undefined,
        opacity: isDisabled ? 0.5 : 1,
      }}
    >
      {/* Card background */}
      <div className="bg-white rounded-3xl overflow-hidden border border-[#e5e9e9] group-hover:border-[#08757C]/40 transition-all duration-300 group-hover:shadow-xl flex flex-col flex-1">
        {/* Top image strip */}
        <div className="relative h-28 overflow-hidden bg-gradient-to-br from-[#08757c] to-[#004f54]">
          {lab.facilityImagePath && !imgError ? (
            <img
              src={`${URL}${lab.facilityImagePath}`}
              alt={lab.facilityName}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="material-symbols-outlined text-white/20 text-[72px]">
                science
              </span>
            </div>
          )}
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex gap-1.5">
            {isMapped && (
              <span className="text-[9px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest bg-[#08757C] text-white backdrop-blur-sm flex items-center gap-1">
                <span className="material-symbols-outlined text-[10px]">
                  star
                </span>
                My Lab
              </span>
            )}
          </div>

          {/* Opening spinner overlay */}
          {isOpening && (
            <div className="absolute inset-0 bg-[#08757C]/60 backdrop-blur-sm flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-[32px] animate-spin">
                progress_activity
              </span>
            </div>
          )}

          {/* Hover arrow */}
          {!isOpening && (
            <div className="absolute bottom-3 right-3 h-8 w-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 translate-x-2 group-hover:translate-x-0">
              <span className="material-symbols-outlined text-white text-[16px]">
                arrow_forward
              </span>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-3 flex flex-col flex-1">
          <h3
            className="font-extrabold text-[#181c1d] text-sm leading-tight truncate"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            {lab.facilityName}
          </h3>
          {lab.projectName && (
            <p
              className="text-[11px] text-[#6e797a] mt-0.5 mb-2"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {lab.projectName}
            </p>
          )}

          {/* Last activity */}
          {lab.createdDate && (
            <p className="text-[10px] text-[#9eabac] mt-2 flex items-center gap-1">
              <span className="material-symbols-outlined text-[11px]">
                history
              </span>
              {lab.createdDate}
              {lab.time ? ` ${lab.time}` : ""}
            </p>
          )}

          {/* Open CTA */}
          <div className="mt-auto pt-2 border-t border-[#f1f4f4] flex items-center justify-between">
            <span className="text-[11px] text-[#9eabac] font-medium">
              {isOpening ? "Opening…" : "Click to open"}
            </span>
            <div
              className="h-7 w-7 rounded-xl flex items-center justify-center transition-all duration-200 group-hover:scale-110"
              style={{ background: isOpening ? "#08757C40" : "#08757C" }}
            >
              <span className="material-symbols-outlined text-white text-[14px]">
                {isOpening ? "hourglass_top" : "arrow_forward"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────
export default function SetupUtilLocations() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [mappedLabs, setMappedLabs] = useState([]);
  const [otherLabs, setOtherLabs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [opening, setOpening] = useState(null);
  const [search, setSearch] = useState("");

  // ── Fetch ────────────────────────────────────
  const fetchList = useCallback(() => {
    setIsLoading(true);
    dispatch(fetchSetupUtilLabList({ companyID, employeeID }))
      .then((res) => {
        const data = res?.payload?.data ?? res?.payload ?? {};
        const mapped = data.mappedLabs ?? [];
        const mappedIds = new Set(mapped.map((l) => l.facilityId));
        setMappedLabs(mapped);
        setOtherLabs(
          (data.allLabs ?? []).filter((l) => !mappedIds.has(l.facilityId)),
        );
      })
      .finally(() => setIsLoading(false));
  }, [dispatch]); // eslint-disable-line

  useEffect(() => {
    fetchList();
  }, [location.key]); // eslint-disable-line

  // ── Open ─────────────────────────────────────
  const handleOpen = (lab) => {
    if (opening) return;
    setOpening(lab.facilityId);
    const today = new Date().toISOString().split("T")[0];

    Promise.all([
      dispatch(
        fetchSetupUtilCreateData({
          facilityID: lab.facilityId,
          companyID,
          employeeID,
        }),
      ),
      dispatch(
        fetchSetupUtilTimesheetList({
          facilityID: lab.facilityId,
          employeeID,
          companyID,
          selDate: today,
        }),
      ),
    ])
      .then(([createRes, timesheetRes]) => {
        const payload = createRes?.payload?.data ?? {};
        const data = payload.data ?? payload;
        const startTime = payload.startTime ?? null;
        const startDate = payload.startDate ?? null;
        const timesheetPayload = timesheetRes?.payload?.data ?? {};
        const timesheetData = timesheetPayload.data ?? timesheetPayload;

        navigate("/lab-management/lab-utilization", {
          state: { lab, createData: data, startTime, startDate, timesheetData },
        });
      })
      .finally(() => setOpening(null));
  };

  // ── Search ────────────────────────────────────
  const q = search.trim().toLowerCase();
  const filter = (labs) =>
    !q
      ? labs
      : labs.filter(
          (l) =>
            (l.facilityName ?? "").toLowerCase().includes(q) ||
            (l.location ?? "").toLowerCase().includes(q),
        );
  const visibleMapped = filter(mappedLabs);
  const visibleOther = filter(otherLabs);
  const total = mappedLabs.length + otherLabs.length;

  return (
    <DashboardLayout>
      <div className="min-h-screen" style={{ background: "#f0f4f4" }}>
        {/* ── Page hero header ── */}
        <div className="px-8 pt-5 pb-4">
          <nav className="flex items-center gap-2 text-xs text-[#6e797a] mb-3">
            <span>Lab Management</span>
            <span className="material-symbols-outlined text-[14px]">
              chevron_right
            </span>
            <span className="font-semibold" style={{ color: "#08757C" }}>
              Setup Utilization
            </span>
          </nav>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1
                className="text-3xl font-black text-[#181c1d] tracking-tight"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Choose a Lab
              </h1>
              <p className="text-sm text-[#6e797a] mt-1">
                {total} lab{total !== 1 ? "s" : ""} available — click any card
                to open
              </p>
            </div>

            {/* Search */}
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9eabac] text-[17px] pointer-events-none">
                search
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search labs…"
                className="h-11 pl-10 pr-5 bg-white border border-[#e5e9e9] rounded-2xl text-sm text-[#181c1d] placeholder:text-[#9eabac] focus:outline-none focus:ring-2 focus:ring-[#08757C]/30 w-60 shadow-sm transition-all"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9eabac] hover:text-[#3e494a]"
                >
                  <span className="material-symbols-outlined text-[16px]">
                    close
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="px-8 pb-8">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4 text-[#9eabac]">
              <span className="material-symbols-outlined text-[40px] animate-spin">
                progress_activity
              </span>
              <p className="text-sm font-medium">Loading labs…</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* ── My Mapped Labs ── */}
              {visibleMapped.length > 0 && (
                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: "#08757C18" }}
                    >
                      <span
                        className="material-symbols-outlined text-[17px]"
                        style={{ color: "#08757C" }}
                      >
                        star
                      </span>
                    </div>
                    <div>
                      <h2 className="text-sm font-extrabold text-[#181c1d]">
                        My Mapped Labs
                      </h2>
                      <p className="text-[11px] text-[#9eabac]">
                        Labs assigned directly to you
                      </p>
                    </div>
                    <span
                      className="ml-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full"
                      style={{ background: "#08757C18", color: "#08757C" }}
                    >
                      {visibleMapped.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {visibleMapped.map((lab) => (
                      <LabCard
                        key={lab.facilityId}
                        lab={lab}
                        isMapped
                        onOpen={handleOpen}
                        opening={opening}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* Divider */}
              {visibleMapped.length > 0 && visibleOther.length > 0 && (
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-[#e5e9e9]" />
                  <span className="text-[10px] font-black text-[#9eabac] uppercase tracking-widest px-1">
                    Other Labs
                  </span>
                  <div className="flex-1 h-px bg-[#e5e9e9]" />
                </div>
              )}

              {/* ── Other Labs ── */}
              {visibleOther.length > 0 && (
                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: "#08757C18" }}
                    >
                      <span
                        className="material-symbols-outlined text-[17px]"
                        style={{ color: "#08757C" }}
                      >
                        lab_panel
                      </span>
                    </div>
                    <div>
                      <h2 className="text-sm font-extrabold text-[#181c1d]">
                        All Labs
                      </h2>
                      <p className="text-[11px] text-[#9eabac]">
                        All labs available in the system
                      </p>
                    </div>
                    <span
                      className="ml-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full"
                      style={{ background: "#08757C18", color: "#08757C" }}
                    >
                      {visibleOther.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {visibleOther.map((lab) => (
                      <LabCard
                        key={lab.facilityId}
                        lab={lab}
                        isMapped={false}
                        onOpen={handleOpen}
                        opening={opening}
                      />
                    ))}
                  </div>
                </section>
              )}

              {/* No search results */}
              {visibleMapped.length === 0 && visibleOther.length === 0 && q && (
                <div className="flex flex-col items-center py-24 gap-3 text-[#9eabac]">
                  <span className="material-symbols-outlined text-[48px]">
                    search_off
                  </span>
                  <p className="text-sm font-bold text-[#3e494a]">
                    No labs match{" "}
                    <span className="text-[#08757C]">"{search}"</span>
                  </p>
                  <button
                    onClick={() => setSearch("")}
                    className="text-xs font-bold px-4 py-2 rounded-xl border border-[#e5e9e9] hover:bg-white transition-all text-[#08757C]"
                  >
                    Clear search
                  </button>
                </div>
              )}

              {/* No labs at all */}
              {total === 0 && !q && (
                <div className="flex flex-col items-center py-32 gap-5">
                  <div
                    className="h-20 w-20 rounded-3xl flex items-center justify-center"
                    style={{ background: "#08757C10" }}
                  >
                    <span
                      className="material-symbols-outlined text-[40px]"
                      style={{ color: "#08757C" }}
                    >
                      lab_panel
                    </span>
                  </div>
                  <div className="text-center">
                    <p className="text-base font-extrabold text-[#181c1d]">
                      No labs available
                    </p>
                    <p className="text-sm text-[#6e797a] mt-1">
                      Contact your administrator to get labs assigned.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
